import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Send } from 'lucide-react';

/** Email/SMTP settings tab — extracted from SettingsPage.jsx (iter790).
 *  iter819 — added Microsoft 365 "Direct Send" mode (unauthenticated,
 *  port 25 → tenant MX endpoint). No IP whitelisting required. */
export function SettingsEmailTab({ emailSettings, setEmailSettings, saveEmail, testEmail, setTestEmail, testEmailSend }) {
  const mode = (emailSettings.smtp_mode || 'auth').toLowerCase();
  const isDirect = mode === 'direct';

  const setMode = (newMode) => {
    // Snap sensible defaults for each mode to avoid user confusion.
    if (newMode === 'direct') {
      setEmailSettings({
        ...emailSettings,
        smtp_mode: 'direct',
        smtp_port: 25,
        username: '',
        password: '',
      });
    } else {
      setEmailSettings({
        ...emailSettings,
        smtp_mode: 'auth',
        smtp_port: emailSettings.smtp_port === 25 ? 587 : (emailSettings.smtp_port || 587),
      });
    }
  };

  return (
    <Card className="border-border">
      <CardHeader><CardTitle className="font-outfit">Email SMTP Configuration</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>Send Mode</Label>
            <select
              className="w-full mt-1 h-10 px-3 rounded-md border border-input bg-background text-sm"
              data-testid="smtp-mode"
              value={mode}
              onChange={(e) => setMode(e.target.value)}
            >
              <option value="auth">SMTP AUTH (username + password)</option>
              <option value="direct">Microsoft 365 Direct Send (no auth, port 25)</option>
            </select>
            <p className="text-xs text-muted-foreground mt-1">
              {isDirect
                ? 'Direct Send routes email directly to your M365 tenant MX endpoint. Requires SPF record + accepted-domain FROM.'
                : 'Standard authenticated SMTP. Common: Gmail (smtp.gmail.com:587), Outlook (smtp-mail.outlook.com:587), SendGrid.'}
            </p>
          </div>
          <div />
          <div>
            <Label>SMTP Host *</Label>
            <Input
              value={emailSettings.smtp_host || ''}
              placeholder={isDirect ? 'yourtenant-com.mail.protection.outlook.com' : 'smtp.office365.com'}
              data-testid="smtp-host"
              onChange={(e) => setEmailSettings({ ...emailSettings, smtp_host: e.target.value })}
            />
          </div>
          <div>
            <Label>SMTP Port</Label>
            <Input
              type="number"
              value={emailSettings.smtp_port || (isDirect ? 25 : 587)}
              data-testid="smtp-port"
              onChange={(e) => setEmailSettings({ ...emailSettings, smtp_port: parseInt(e.target.value) || (isDirect ? 25 : 587) })}
            />
          </div>
          {!isDirect && (
            <>
              <div>
                <Label>Username / Email *</Label>
                <Input
                  value={emailSettings.username || ''}
                  placeholder="your@email.com"
                  data-testid="smtp-user"
                  onChange={(e) => setEmailSettings({ ...emailSettings, username: e.target.value })}
                />
              </div>
              <div>
                <Label>Password / App Password *</Label>
                <Input
                  type="password"
                  value={emailSettings.password || ''}
                  placeholder="••••••••"
                  data-testid="smtp-pass"
                  onChange={(e) => setEmailSettings({ ...emailSettings, password: e.target.value })}
                />
              </div>
            </>
          )}
          <div>
            <Label>From Email {isDirect && <span className="text-xs text-muted-foreground">(must be accepted domain in M365)</span>}</Label>
            <Input
              value={emailSettings.from_email || ''}
              placeholder="noreply@yourcompany.com"
              data-testid="smtp-from"
              onChange={(e) => setEmailSettings({ ...emailSettings, from_email: e.target.value })}
            />
          </div>
        </div>
        <div className="flex flex-wrap gap-3 pt-2 items-center">
          <Button onClick={saveEmail} className="rounded-full" data-testid="save-email">Save Email Settings</Button>
          <div className="flex gap-2 items-center">
            <Input value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="test@email.com" className="w-48 h-9" data-testid="smtp-test-to" />
            <Button variant="outline" onClick={testEmailSend} className="rounded-full" data-testid="test-email"><Send size={14} className="mr-2" />Send Test</Button>
          </div>
        </div>
        {isDirect && (
          <div className="rounded-md border border-amber-200 bg-amber-50 dark:bg-amber-950/30 p-3 text-xs text-amber-900 dark:text-amber-200">
            <strong>Microsoft 365 Direct Send checklist:</strong>
            <ol className="list-decimal ml-4 mt-1 space-y-0.5">
              <li>Sending domain has an SPF record permitting this app (or set to <code>~all</code>).</li>
              <li>FROM address uses a domain listed as <em>Accepted Domain</em> in the M365 tenant.</li>
              <li>Outbound port 25 to <code>*.mail.protection.outlook.com</code> reachable from the app.</li>
              <li>Direct Send only delivers to recipients in the same M365 tenant unless an SMTP relay connector is configured.</li>
            </ol>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default SettingsEmailTab;
